import { useState, useEffect, useCallback, useRef } from "react";
import { debounce } from "lodash";

export const useCuotasMonitor = (proyeccionData, disponible, configClases, clasesVinculadas) => {
  const [excesos, setExcesos] = useState({});
  const [isMonitoring, setIsMonitoring] = useState(false);
  const analizisEnProgreso = useRef(false);

  const analizarCuotas = useCallback(
    debounce((proyeccion, disponibleTotal, config, vinculaciones) => {
      if (!proyeccion?.proyeccion || !disponibleTotal || analizisEnProgreso.current) return;

      try {
        analizisEnProgreso.current = true;
        const nuevosExcesos = {};
        const excesosVinculados = {};

        // Analizar clases vinculadas
        Object.entries(vinculaciones).forEach(([clase, clasesVinculadas]) => {
          if (!excesosVinculados[clase]) {
            let excedentesConsecutivos = [];
            let excedenteTemporal = null;

            const proyeccionClase = proyeccion.proyeccion[clase]?.proyeccion || [];

            proyeccionClase.forEach((cuota, index) => {
              let valorTotal = cuota.cuota;

              // Sumar valores de clases vinculadas
              clasesVinculadas.forEach((claseVinculada) => {
                if (proyeccion.proyeccion[claseVinculada]?.proyeccion[index]) {
                  valorTotal += proyeccion.proyeccion[claseVinculada].proyeccion[index].cuota;
                }
              });

              if (valorTotal > disponibleTotal) {
                if (!excedenteTemporal) {
                  excedenteTemporal = {
                    cuotaInicial: cuota.numero_cuota,
                    valorCuota: valorTotal,
                    excedente: valorTotal - disponibleTotal,
                    clasesVinculadas,
                  };
                } else {
                  // Actualizar el excedente si es mayor
                  if (valorTotal - disponibleTotal > excedenteTemporal.excedente) {
                    excedenteTemporal.excedente = valorTotal - disponibleTotal;
                    excedenteTemporal.valorCuota = valorTotal;
                  }
                }
              } else if (excedenteTemporal) {
                excedenteTemporal.cuotaFinal = proyeccionClase[index - 1].numero_cuota;
                excedentesConsecutivos.push(excedenteTemporal);
                excedenteTemporal = null;
              }
            });

            if (excedenteTemporal) {
              excedenteTemporal.cuotaFinal =
                proyeccionClase[proyeccionClase.length - 1].numero_cuota;
              excedentesConsecutivos.push(excedenteTemporal);
            }

            // Consolidar excesos consecutivos
            const excesosConsolidados = [];
            let excesoActual = null;

            excedentesConsecutivos.forEach((exceso) => {
              if (!excesoActual) {
                excesoActual = { ...exceso };
              } else if (exceso.cuotaInicial === excesoActual.cuotaFinal + 1) {
                // Extender el rango si son consecutivos
                excesoActual.cuotaFinal = exceso.cuotaFinal;
                excesoActual.excedente = Math.max(excesoActual.excedente, exceso.excedente);
                excesoActual.valorCuota = Math.max(excesoActual.valorCuota, exceso.valorCuota);
              } else {
                excesosConsolidados.push(excesoActual);
                excesoActual = { ...exceso };
              }
            });

            if (excesoActual) {
              excesosConsolidados.push(excesoActual);
            }

            if (excesosConsolidados.length > 0) {
              excesosVinculados[clase] = {
                excedentes: excesosConsolidados,
                tipo: "VINCULADA",
                clasesVinculadas,
              };

              clasesVinculadas.forEach((claseVinculada) => {
                excesosVinculados[claseVinculada] = {
                  tipo: "VINCULADA_REFERENCIA",
                  claseReferencia: clase,
                };
              });
            }
          }
        });

        // Analizar excesos individuales (mantener la lógica existente)
        Object.entries(proyeccion.proyeccion).forEach(([clase, datos]) => {
          if (!datos?.proyeccion || excesosVinculados[clase]) return;

          let excedentesConsecutivos = [];
          let excedenteTemporal = null;

          datos.proyeccion.forEach((cuota) => {
            const valorTotal = cuota.cuota;

            if (valorTotal > disponibleTotal) {
              if (!excedenteTemporal) {
                excedenteTemporal = {
                  cuotaInicial: cuota.numero_cuota,
                  valorCuota: valorTotal,
                  excedente: valorTotal - disponibleTotal,
                };
              } else {
                // Actualizar el excedente si es mayor
                if (valorTotal - disponibleTotal > excedenteTemporal.excedente) {
                  excedenteTemporal.excedente = valorTotal - disponibleTotal;
                  excedenteTemporal.valorCuota = valorTotal;
                }
              }
            } else if (excedenteTemporal) {
              excedenteTemporal.cuotaFinal = cuota.numero_cuota - 1;
              excedentesConsecutivos.push(excedenteTemporal);
              excedenteTemporal = null;
            }
          });

          if (excedenteTemporal) {
            excedenteTemporal.cuotaFinal =
              datos.proyeccion[datos.proyeccion.length - 1].numero_cuota;
            excedentesConsecutivos.push(excedenteTemporal);
          }

          if (excedentesConsecutivos.length > 0) {
            nuevosExcesos[clase] = {
              excedentes: excedentesConsecutivos,
              tipo: "INDIVIDUAL",
            };
          }
        });

        setExcesos({ ...nuevosExcesos, ...excesosVinculados });
      } finally {
        analizisEnProgreso.current = false;
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (proyeccionData?.proyeccion && disponible && configClases) {
      setIsMonitoring(true);
      analizarCuotas(proyeccionData, disponible, configClases, clasesVinculadas);
    }
    return () => {
      analizarCuotas.cancel();
      setIsMonitoring(false);
    };
  }, [proyeccionData, disponible, configClases, clasesVinculadas, analizarCuotas]);

  return {
    excesos,
    isMonitoring,
    hayExcesos:
      Object.keys(excesos).filter((clase) => excesos[clase].tipo !== "VINCULADA_REFERENCIA")
        .length > 0,
  };
};
